import { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  AcUnit as FreezeIcon,
  WbSunny as UnfreezeIcon,
  SwapHoriz as ChangeStateIcon,
  Cancel as CancelIcon,
  Restore as RestoreIcon,
  Undo as UndoIcon,
  AltRoute as RerouteIcon,
} from '@mui/icons-material';
import { DeliveryState } from '../../../enums/orders';

export interface OrderAction {
  key: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  tooltip?: string;
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  variant?: 'contained' | 'outlined' | 'text';
  hide?: boolean;
}

interface OrderActionsHeaderProps {
  primaryActions?: OrderAction[];
  secondaryActions?: OrderAction[];
}

export default function OrderActionsHeader({
  primaryActions = [],
  secondaryActions = [],
}: OrderActionsHeaderProps) {
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null);

  const handleMoreMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMoreMenuAnchor(event.currentTarget);
  };

  const handleMoreMenuClose = () => {
    setMoreMenuAnchor(null);
  };

  const handleSecondaryAction = (action: OrderAction) => {
    action.onClick();
    handleMoreMenuClose();
  };

  const visiblePrimaryActions = primaryActions.filter((action) => !action.hide);
  const visibleSecondaryActions = secondaryActions.filter((action) => !action.hide);

  // Always show all primary actions as buttons (no collapsing on small screens)
  // If there's only one secondary action, show it as a button instead of using "More" menu
  const shouldUseMoreMenu = visibleSecondaryActions.length > 1;
  const finalPrimaryActions = shouldUseMoreMenu 
    ? visiblePrimaryActions 
    : [...visiblePrimaryActions, ...visibleSecondaryActions];
  const finalSecondaryActions = shouldUseMoreMenu ? visibleSecondaryActions : [];
  const hasSecondaryActions = finalSecondaryActions.length > 0;

  if (finalPrimaryActions.length === 0 && finalSecondaryActions.length === 0) {
    return null;
  }

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        gap: 1, 
        alignItems: 'flex-start', 
        flexWrap: 'wrap',
        justifyContent: 'flex-start',
        width: '100%',
      }}
    >
      {/* Primary Actions - Always visible, wrap to multiple rows if needed */}
      {finalPrimaryActions.map((action) => {
        const button = (
          <Button
            key={action.key}
            variant={action.variant || 'contained'}
            color={action.color || 'primary'}
            onClick={action.onClick}
            disabled={action.disabled}
            startIcon={action.icon}
            size="small"
            sx={{
              minWidth: 'auto',
              px: 1.5,
              py: 0.5,
              fontSize: '0.8125rem',
              textTransform: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            {action.label}
          </Button>
        );

        return action.tooltip && action.disabled ? (
          <Tooltip key={action.key} title={action.tooltip}>
            <span>{button}</span>
          </Tooltip>
        ) : (
          button
        );
      })}

      {/* More Actions Menu - Only shown when there are 2+ secondary actions */}
      {hasSecondaryActions && (
        <>
          <Tooltip title="More actions">
            <Button
              onClick={handleMoreMenuOpen}
              variant="outlined"
              size="small"
              startIcon={<MoreVertIcon />}
              sx={{
                minWidth: 'auto',
                px: 1.5,
                py: 0.5,
                fontSize: '0.8125rem',
                textTransform: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              More
            </Button>
          </Tooltip>

          <Menu
            anchorEl={moreMenuAnchor}
            open={Boolean(moreMenuAnchor)}
            onClose={handleMoreMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 200,
              },
            }}
          >
            {finalSecondaryActions.map((action) => {
              const menuItem = (
                <MenuItem
                  key={action.key}
                  onClick={() => handleSecondaryAction(action)}
                  disabled={action.disabled}
                  sx={{
                    gap: 1.5,
                    py: 1.5,
                    color: action.color === 'error' ? 'error.main' : undefined,
                  }}
                >
                  {action.icon}
                  {action.label}
                </MenuItem>
              );

              return action.tooltip && action.disabled ? (
                <Tooltip key={action.key} title={action.tooltip} placement="left">
                  {menuItem}
                </Tooltip>
              ) : (
                menuItem
              );
            })}
          </Menu>
        </>
      )}
    </Box>
  );
}

// Helper function to create freeze/unfreeze action
export function createFreezeAction(
  isFrozen: boolean,
  canUpdate: boolean,
  deliveryState: DeliveryState,
  onFreeze: () => void,
  onUnfreeze: () => void
): OrderAction {
  if (isFrozen) {
    return {
      key: 'unfreeze',
      label: 'Unfreeze',
      icon: <UnfreezeIcon />,
      onClick: onUnfreeze,
      disabled: !canUpdate,
      color: 'warning',
      variant: 'contained',
    };
  }

  const canFreeze =
    canUpdate &&
    [DeliveryState.CREATED, DeliveryState.AT_WAREHOUSE].includes(deliveryState);

  return {
    key: 'freeze',
    label: 'Freeze',
    icon: <FreezeIcon />,
    onClick: onFreeze,
    disabled: !canFreeze,
    tooltip: !canFreeze ? 'Order can only be frozen when created or at warehouse' : undefined,
    color: 'primary',
    variant: 'outlined',
  };
}

// Helper function to create standard actions
export const createEditAction = (
  canUpdate: boolean,
  onClick: () => void,
  disabled = false
): OrderAction => ({
  key: 'edit',
  label: 'Edit Order',
  icon: <EditIcon />,
  onClick,
  disabled: !canUpdate || disabled,
  color: 'primary',
  variant: 'contained',
});

export const createChangeStateAction = (
  canUpdate: boolean,
  onClick: () => void,
  disabled = false
): OrderAction => ({
  key: 'change-state',
  label: 'Change State',
  icon: <ChangeStateIcon />,
  onClick,
  disabled: !canUpdate || disabled,
  color: 'primary',
  variant: 'outlined',
});

export const createCancelAction = (
  canDelete: boolean,
  onClick: () => void,
  disabled = false
): OrderAction => ({
  key: 'cancel',
  label: 'Cancel Order',
  icon: <CancelIcon />,
  onClick,
  disabled: !canDelete || disabled,
  color: 'error',
  variant: 'outlined',
});

export const createRestoreAction = (onClick: () => void): OrderAction => ({
  key: 'restore',
  label: 'Restore Order',
  icon: <RestoreIcon />,
  onClick,
  color: 'success',
  variant: 'outlined',
});

export const createUndoDeliveryAction = (
  onClick: () => void,
  disabled = false,
  tooltip?: string
): OrderAction => ({
  key: 'undo-delivery',
  label: 'Undo Delivery',
  icon: <UndoIcon />,
  onClick,
  disabled,
  tooltip,
  color: 'error',
  variant: 'outlined',
});

export const createRerouteAction = (
  onClick: () => void,
  canUpdate: boolean,
  deliveryState: DeliveryState,
  tooltip?: string
): OrderAction => {
  const canReroute = canUpdate && deliveryState === DeliveryState.AT_WAREHOUSE;
  return {
    key: 'reroute',
    label: 'Reroute',
    icon: <RerouteIcon />,
    onClick,
    disabled: !canReroute,
    tooltip: tooltip || (!canReroute ? 'Order can only be rerouted when at warehouse' : 'Change how this order is routed for delivery'),
    color: 'warning',
    variant: 'outlined',
  };
};

